import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  deleteDoc,
  updateDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  limit,
} from 'firebase/firestore';
import { db, appId } from '@/config/firebase';
import type {
  Playlist,
  PlaylistTrack,
  AudioTrack,
  CreatePlaylistInput,
  UpdatePlaylistInput,
} from '@/types/audio';
import { audioTrackToPlaylistTrack } from '@/types/audio'; // eslint-disable-line no-duplicate-imports

// Collection path helper
const getUserPlaylistsPath = (userId: string) => `artifacts/${appId}/users/${userId}/playlists`;

// ==================== Playlist CRUD ====================

/**
 * Create a new playlist
 */
export async function createPlaylist(userId: string, input: CreatePlaylistInput): Promise<string> {
  const playlistsRef = collection(db, getUserPlaylistsPath(userId));
  const now = Timestamp.now();

  // Convert AudioTracks to PlaylistTracks if provided
  const tracks: PlaylistTrack[] = input.tracks
    ? input.tracks.map((track) => audioTrackToPlaylistTrack(track, now))
    : [];

  const playlistData = {
    name: input.name.trim(),
    description: input.description?.trim() || null,
    tracks,
    trackCount: tracks.length,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const docRef = await addDoc(playlistsRef, playlistData);
  return docRef.id;
}

/**
 * Get a single playlist by ID
 */
export async function getPlaylist(userId: string, playlistId: string): Promise<Playlist | null> {
  const playlistRef = doc(db, getUserPlaylistsPath(userId), playlistId);
  const snapshot = await getDoc(playlistRef);

  if (!snapshot.exists()) {
    return null;
  }

  return {
    id: snapshot.id,
    ...snapshot.data(),
  } as Playlist;
}

/**
 * Get all playlists for a user
 */
export async function getPlaylists(userId: string): Promise<Playlist[]> {
  const playlistsRef = collection(db, getUserPlaylistsPath(userId));
  const q = query(playlistsRef, orderBy('updatedAt', 'desc'), limit(100));
  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Playlist[];
}

/**
 * Update a playlist
 */
export async function updatePlaylist(
  userId: string,
  playlistId: string,
  updates: UpdatePlaylistInput
): Promise<void> {
  const playlistRef = doc(db, getUserPlaylistsPath(userId), playlistId);

  const updateData: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
  };

  if (updates.name !== undefined) {
    updateData.name = updates.name.trim();
  }

  if (updates.description !== undefined) {
    updateData.description = updates.description?.trim() || null;
  }

  if (updates.tracks !== undefined) {
    updateData.tracks = updates.tracks;
    updateData.trackCount = updates.tracks.length;
  }

  await updateDoc(playlistRef, updateData);
}

/**
 * Delete a playlist
 */
export async function deletePlaylist(userId: string, playlistId: string): Promise<void> {
  const playlistRef = doc(db, getUserPlaylistsPath(userId), playlistId);
  await deleteDoc(playlistRef);
}

// ==================== Track Operations ====================

/**
 * Add tracks to a playlist
 */
export async function addTracksToPlaylist(
  userId: string,
  playlistId: string,
  tracks: AudioTrack[]
): Promise<void> {
  const playlist = await getPlaylist(userId, playlistId);
  if (!playlist) {
    throw new Error('Playlist not found');
  }

  const now = Timestamp.now();
  const newTracks = tracks.map((track) => audioTrackToPlaylistTrack(track, now));
  const updatedTracks = [...playlist.tracks, ...newTracks];

  await updatePlaylist(userId, playlistId, { tracks: updatedTracks });
}

/**
 * Remove a track from a playlist by index
 */
export async function removeTrackFromPlaylist(
  userId: string,
  playlistId: string,
  trackIndex: number
): Promise<void> {
  const playlist = await getPlaylist(userId, playlistId);
  if (!playlist) {
    throw new Error('Playlist not found');
  }

  if (trackIndex < 0 || trackIndex >= playlist.tracks.length) {
    throw new Error('Invalid track index');
  }

  const updatedTracks = [
    ...playlist.tracks.slice(0, trackIndex),
    ...playlist.tracks.slice(trackIndex + 1),
  ];

  await updatePlaylist(userId, playlistId, { tracks: updatedTracks });
}

/**
 * Reorder tracks within a playlist
 */
export async function reorderPlaylistTracks(
  userId: string,
  playlistId: string,
  fromIndex: number,
  toIndex: number
): Promise<void> {
  const playlist = await getPlaylist(userId, playlistId);
  if (!playlist) {
    throw new Error('Playlist not found');
  }

  if (
    fromIndex < 0 ||
    fromIndex >= playlist.tracks.length ||
    toIndex < 0 ||
    toIndex >= playlist.tracks.length
  ) {
    throw new Error('Invalid track indices');
  }

  const tracks = [...playlist.tracks];
  const [movedTrack] = tracks.splice(fromIndex, 1);
  tracks.splice(toIndex, 0, movedTrack);

  await updatePlaylist(userId, playlistId, { tracks });
}

// ==================== Real-time Subscriptions ====================

/**
 * Subscribe to user's playlists (real-time updates)
 */
export function subscribeToPlaylists(
  userId: string,
  callback: (playlists: Playlist[]) => void
): () => void {
  const playlistsRef = collection(db, getUserPlaylistsPath(userId));
  const q = query(playlistsRef, orderBy('updatedAt', 'desc'));

  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      const playlists = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Playlist[];
      callback(playlists);
    },
    (error) => {
      console.error('Error subscribing to playlists:', error);
    }
  );

  return unsubscribe;
}

/**
 * Subscribe to a single playlist (real-time updates)
 */
export function subscribeToPlaylist(
  userId: string,
  playlistId: string,
  callback: (playlist: Playlist | null) => void
): () => void {
  const playlistRef = doc(db, getUserPlaylistsPath(userId), playlistId);

  const unsubscribe = onSnapshot(
    playlistRef,
    (snapshot) => {
      if (!snapshot.exists()) {
        callback(null);
        return;
      }
      callback({
        id: snapshot.id,
        ...snapshot.data(),
      } as Playlist);
    },
    (error) => {
      console.error('Error subscribing to playlist:', error);
    }
  );

  return unsubscribe;
}

// ==================== Utility Functions ====================

/**
 * Create a playlist from the current queue
 */
export async function createPlaylistFromQueue(
  userId: string,
  name: string,
  queue: AudioTrack[]
): Promise<string> {
  return createPlaylist(userId, { name, tracks: queue });
}

/**
 * Check if a playlist exists
 */
export async function playlistExists(userId: string, playlistId: string): Promise<boolean> {
  const playlist = await getPlaylist(userId, playlistId);
  return playlist !== null;
}
